import { Injectable } from '@angular/core';
import { OkAloneService } from '../ok-alone.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { getPointCoordinates, isPointGeometry, isPolygonGeometry } from '../pages/mgmt/threats/threat-geometry.util';

export interface WeatherAlertItem {
  id: string;
  title: string;
  severity: string;
  /** Factal numeric severity (1–5); null for weather/custom. */
  severityInt: number | null;
  event: string;
  areaDesc: string;
  effective: string;
  expires: string;
  headline: string;
  description: string;
  /** Lowercase DB category (weather, factal, civil_unrest, …). */
  category: string;
  /** Lowercase DB source (weather, custom, factal). */
  source: string;
  centroid: { lng: number; lat: number };
  geometry: any;
  /** True when geometry is a Point (pin threat). */
  isPin: boolean;
  /** ISO 3166-1 alpha-2 from API when present. */
  country?: string;
}

export interface WeatherAlertsQuery {
  category?: string;
  source?: string;
  /** Omit or empty = API uses company default; `all` disables country filter. */
  country?: string;
  /** Omit or empty = API default (100 mi); `all` disables proximity. */
  workerProximity?: string;
}

@Injectable({
  providedIn: 'root'
})
export class WeatherAlertsService {

  constructor(private okService: OkAloneService) {}

  getActiveAlerts(opts: WeatherAlertsQuery = {}): Observable<WeatherAlertItem[]> {
    const params: Record<string, string> = { active: '1', country: 'all' };
    if (opts.category) {
      params['category'] = opts.category;
    }
    if (opts.source) {
      params['source'] = opts.source;
    }
    if (opts.workerProximity !== undefined && opts.workerProximity !== '') {
      params['worker_proximity'] = opts.workerProximity;
    }

    return this.okService.get('threatzones', params).pipe(
      map((response: any) => this.transformApiResponse(response.body || [])),
      catchError(error => {
        console.error('WeatherAlertsService: error fetching threat zones from backend', error);
        return of([]);
      })
    );
  }

  private hasMapGeometry(t: any): boolean {
    const geometry = t.geometry;
    if (!geometry?.coordinates) {
      return false;
    }
    if (isPointGeometry(geometry)) {
      return getPointCoordinates(geometry) !== null;
    }
    if (isPolygonGeometry(geometry)) {
      return Array.isArray(geometry.coordinates) && geometry.coordinates.length > 0;
    }
    return false;
  }

  private transformApiResponse(data: any[]): WeatherAlertItem[] {
    if (!Array.isArray(data)) {
      console.warn('[WeatherAlertsService] transformApiResponse: response is not an array');
      return [];
    }

    const withGeom = data.filter(t => this.hasMapGeometry(t));
    console.log(`[WeatherAlertsService] Backend returned ${data.length} threat zones, ${withGeom.length} with map geometry`);

    return withGeom.map((t: any): WeatherAlertItem => {
      const rawCat = t.category ?? 'custom';
      const rawSrc = t.source ?? 'custom';
      const sevInt = t.severity_int;
      const isPin = t.is_pin === true || t.is_pin === 1 || isPointGeometry(t.geometry);
      return {
        id: String(t.id),
        title: t.headline || t.event || 'Threat',
        severity: t.severity || 'Unknown',
        severityInt: sevInt !== undefined && sevInt !== null ? Number(sevInt) : null,
        event: t.event || 'Unknown',
        areaDesc: t.area_desc ?? t.areaDesc ?? '',
        effective: t.effective || '',
        expires: t.expires || '',
        headline: t.headline || '',
        description: t.description || '',
        category: String(rawCat).toLowerCase(),
        source: String(rawSrc).toLowerCase(),
        centroid: {
          lng: t.centroid_lng ?? t.centroidLng ?? 0,
          lat: t.centroid_lat ?? t.centroidLat ?? 0
        },
        geometry: t.geometry,
        isPin,
        country: t.country != null && t.country !== '' ? String(t.country).toUpperCase() : undefined
      };
    });
  }
}
